import {
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
  useRevalidator
} from "react-router-dom";
import { FieldValues, useForm } from "react-hook-form";
import { format } from "date-fns";
import { EditorView } from "@codemirror/view";

import { RiErrorWarningFill } from "@remixicon/react";

import { ReactComponent as XIcon } from "assets/icons/x-icon.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";

import scraperApi, { ApiError, ApiErrorResponse } from "api";
import { TABLE_CONFIGS } from "utils/analytics/tableConfigs";
import { cx, fmtCurrency, fmtDate } from "utils";
import { isLegacyAnnualPlan, manualRenewToThisIfScheduled } from "utils/planUtils";

import BookSalesCallModalContent from "components/BookSalesCallModalContent";
import CouponCodeInput from "components/CouponCodeInput";
import { useFeatureSwitch } from "components/FeatureSwitch";
import FormSubmittingSpinner from "components/FormSubmittingSpinner";
import Button from "components/Button";
import Form from "components/Form";
import Spinner from "components/Spinner";
import SubmitButton from "components/SubmitButton";
import TaggedText from "components/TaggedText";
import PasswordChangeForm from "components/PasswordChangeForm";
import Toaster from "components/Toaster";

import { useChargebeePortals } from "hooks/useChargebeePortals";
import useFormattedActionError from "hooks/useFormattedActionError";
import { useStatefulNavigate } from "hooks/useStatefulNavigate";

import Messages from "misc/Messages";

import { useIssues } from "providers/IssuesProvider";

import { RouteErrors } from "routes/data-routes/types";
import { useUser } from "routes/dataroutes/UserData";

import OldUpgradeSubscriptionModal from "./OldUpgradeSubscriptionModal";

import {
  CancellationSurveyModalContent,
  CancelSubscriptionModalContent,
  CheckingUnpaidInvoicesModalContent,
  DisabledBecauseOfUnpaidInvoicesModalContent,
} from "./CancelSubscription";
import { ErrorMessage } from "./ErrorMessage";

import ErrorFiltersModalContent, {
  FiltersModalContent,
  CustomizeColumnsModalContent,
  ErrorFilterSelection,
} from "./AnalyticsModal";

import ExtLink from "components/ExtLink";
import { trackBillingClickAway, trackRenewalChanges } from "utils/Tracking";

import { path as HomePath } from "v2/pages/home/HomeRoute";
import { useErrorLogs } from "providers/AnalyticsProvider";
import { useAnalyticsFilters } from "providers/AnalyticsProvider/AnalyticsFilterProvider";
import { CodeView } from "components/CodeView";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import Card from "components/Card";
import { SEVERITY_LEVELS, SeverityLevel } from "utils/analytics";
import { analyticsCodeViewTheme } from "utils/analytics/themes";
import { usePlanHelpers } from "routes/data-routes/billing-data/plans/usePlanHelpers";
import Plan from "types/Plan";
import ActiveSubscription from "types/ActiveSubscription";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { useDarkModeForNewDesign } from "hooks/useDarkModeForNewDesign";


// Re-export
export {
  HostedScraperErrorReportModal,
  HostedScraperCancelJobModal,
  HostedScraperJobInfoModal,
} from "./HostedScraperModals";


function SunsettingRenewalWarningCard(
  {
    auto = false
  }: {
    auto?: boolean;
  }
) {
  return (
    <Card className="bg-brandPrimary-50 dark:bg-primary-50 mb-4">
      <div className="flex flex-row items-center gap-x-4">
        <RiErrorWarningFill className="size-11 flex-shrink-0 text-brandPrimary-600 dark:text-primary-600" />
        <div className="font-semibold">You can continue { auto && "auto-" }renewing your plan before your billing date until December 2025, after which this feature will no longer be available.</div>
      </div>
    </Card>
  );
}

interface IModalProps {
  headline?: ReactNode;
  wrapHeadline?: boolean;
  children: ReactElement<any, any> | ReactElement<any, any>[];
  onClose?: () => void;
  closeOnEscapeKey?: boolean;
  closeOnClickAway?: boolean;
  showOverflow?: boolean;
  hideClose?: boolean;
  show?: boolean;
  className?: string;
}

export const isCouponError = (apiError: ApiError<ApiErrorResponse>) => {
  return [
    "err_fp_user_not_included",
    "err_fp_coupon_mismatch",
    "err_fp_user_denied",
    "err_fp_promo_not_active",
    "err_fp_no_offer",
    "err_coupon_not_active",
    "err_coupon_not_applicable",
    "err_coupon_not_supported",
  ].includes(apiError.error_code);
};

/**
 * @deprecated Can't return the discount prices properly. Should find an alternative, like calling `getDiscountPriceFor` or using the `useDiscountPrice` hook.
 * @param plan
 * @param subscription
 */
export const getTotalPriceToInvoice = (
  plan?: Plan,
  subscription?: ActiveSubscription | null
) => {
  return (
    (manualRenewToThisIfScheduled(subscription?.scheduledSubscription?.planSlug) &&
        // TODO no discount price here.. doesn't really matter as this helper should go away anyway
        subscription?.price
      // (subscription?.scheduledSubscription?.discount_price ||
      //   subscription?.scheduledSubscription?.plan_unit_price)
    ) ||
    plan?.price ||
    subscription?.price ||
    0
  );
};

/**
 * This should be used for modals that are bound to a URL
 */
interface CloseModalOptions {
  blockNavigationIf?: boolean;  // TODO with using this property, the callback will get re-generated every time this boolean is changed (i guess).. safer to use a callback here to avoid those unnecessary regenerations
  blockNavigationCallback?: () => boolean;
  goBackInHistoryOnClose?: boolean;
}

export const useCloseModal = (opts?: CloseModalOptions) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location?.state as { backgroundLocation?: Location };

  const closeModal = useCallback(() => {
    if (opts?.blockNavigationIf) {
      return false;
    }

    if (opts?.blockNavigationCallback?.()) {
      return false;
    }

    if (opts?.goBackInHistoryOnClose) {
      navigate(-1);
    } else {
      const prevPath = state?.backgroundLocation || HomePath;
      navigate(prevPath, { viewTransition: true });
    }
    return true;
  }, [state?.backgroundLocation, navigate, opts]);

  return closeModal;
};

export function ErrorRequestModal() {
  const closeModal = useCloseModal();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  const state = location.state as {
    requestContent?: string;
  };

  const content = state?.requestContent || "No request data available";

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      onClose={closeModal}
      showOverflow
      closeOnEscapeKey
      closeOnClickAway
    >
      <div className="bg-[#001531] rounded-lg overflow-hidden border border-[#00244b] min-w-[800px]">
        <div className="flex justify-end px-4 pt-4 pb-3">
          <Button
            onClick={handleCopy}
            className="!bg-[#7100c7] hover:!bg-[#8500e5] button button-primary"
            text={copied ? "Copied!" : "Copy"}
            size="SM"
          />
        </div>

        <div className="px-4 pb-6">
          <CodeView
            language="text"
            content={content}
            minHeight="20vh"
            maxHeight="60vh"
            editable={false}
            theme={analyticsCodeViewTheme}
            extensions={[EditorView.lineWrapping]}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              dropCursor: false,
              allowMultipleSelections: false,
              indentOnInput: false,
              bracketMatching: true,
              closeBrackets: false,
              autocompletion: false,
              rectangularSelection: false,
              highlightSelectionMatches: false,
              searchKeymap: false,
            }}
          />
        </div>
      </div>
    </Modal>
  );
}

export default function Modal({
  headline,
  wrapHeadline = true,
  children,
  onClose,
  closeOnEscapeKey,
  closeOnClickAway = false,
  showOverflow,
  show,
  className,
}: IModalProps) {
  const useDarkMode = useDarkModeForNewDesign();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef(null);

  const closeOnEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose && closeOnEscapeKey) {
        onClose();
      }
    },
    [onClose, closeOnEscapeKey]
  );

  const handleClickAway = useCallback(
    (e: MouseEvent) => {
      if (
        closeOnClickAway &&
        onClose &&
        panelRef.current &&
        !panelRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    },
    [onClose, closeOnClickAway]
  );

  useEffect(() => {
    setIsOpen(show === undefined ? true : show);
  }, [ show ]);

  useEffect(() => {
    if (closeOnEscapeKey) {
      document.addEventListener("keydown", closeOnEsc);
      return () => document.removeEventListener("keydown", closeOnEsc);
    }
  }, [onClose, closeOnEscapeKey, closeOnEsc]);

  useEffect(() => {
    if (closeOnClickAway) {
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleClickAway);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleClickAway);
      };
    }
  }, [closeOnClickAway, handleClickAway]);

  return (
    <Transition.Root show={ isOpen } as={ Fragment }>
      <Dialog
        as="div"
        className="relative z-30"
        onClose={ () => {} }
        initialFocus={ focusRef }
      >
        <Transition.Child
          as={ Fragment }
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={ `fixed inset-0 transition-opacity bg-gray-500 dark:bg-neutral-500 bg-opacity-75 ${ closeOnClickAway ? '' : 'pointer-events-none' }` }
          />
        </Transition.Child>

        <div className="fixed inset-0 z-30 overflow-y-auto" ref={ focusRef }>
          <div className="flex items-end justify-center min-h-full p-4 sm:items-center sm:p-0">
            <Transition.Child
              as={ Fragment }
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                ref={ panelRef }
                className={ cx(
                  className,
                  useDarkMode && "dark",
                  showOverflow ? "overflow-visible" : "overflow-hidden",
                  "relative w-full transition-all transform bg-white sm:min-w-[500px] sm:max-w-min dark:rounded"
                ) }
              >
                <div className="relative">
                  { headline && (
                    <Dialog.Title
                      as="span"
                      className={ cx(
                        "text-lg dark:text-base dark:font-medium leading-6 text-brandDarkest dark:text-primary-800 w-full flex items-center px-5 dark:pt-5 dark:pb-2 py-4 border-b border-b-borderColor dark:border-b-0",
                        !wrapHeadline && "text-nowrap",
                        (!wrapHeadline && !!onClose) && "mr-11 dark:mr-9"
                      ) }
                    >
                      { headline }
                    </Dialog.Title>
                  ) }

                  { onClose && (
                    <XIcon
                      className="absolute right-4 top-4 dark:right-5 dark:top-5 size-8 dark:size-6 p-1.5 transition-colors rounded-sm cursor-pointer text-gray dark:text-neutral-500 hover:text-gray-800 dark:hover:text-neutral-800 hover:bg-slate-100"
                      onClick={ onClose }
                    />
                  ) }
                  { children }
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export function ModalContent({
  children,
  smallerGapFromButtons,
  fullWidthChildren = false,
}: {
  children?: React.ReactNode;
  smallerGapFromButtons?: boolean;
  fullWidthChildren?: boolean;
}) {
  const padding = fullWidthChildren ? "" : "p-5";
  return (
    <div
      className={`flex flex-col ${padding} overflow-visible ${
        smallerGapFromButtons ? "gap-y-4" : "gap-y-12 dark:gap-y-6"
      }`}
    >
      {children}
    </div>
  );
}

export function ModalSections({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-y-4 text-gray dark:text-neutral-600">
      {children}
    </div>
  );
}

export function ModalButtonsSection({
  children,
  justifyBetween,
  className,
}: {
  children?: React.ReactNode;
  justifyBetween?: string;
  className?: string;
}) {
  const finalClassName = cx(`flex ${
        justifyBetween ? "justify-between" : "justify-end"
      } gap-x-4 items-center`, className);
  return (
    <div
      className={finalClassName}
    >
      {children}
    </div>
  );
}

export function ChangePasswordModal() {
  const user = useUser();
  const revalidator = useRevalidator();
  const methods = useForm({
    mode: "onChange",
  });
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });

  const changePasswordCallback = useCallback(
    async (oldPassword: string, newPassword: string) => {
      await scraperApi.auth.changePassword(oldPassword, newPassword);
      // TODO probably there's no need to reload the data here. changing a password should not affect anything
      revalidator.revalidate();
      goBack();
    },
    [goBack, revalidator]
  );

  return (
    <Modal
      headline={user?.hasPassword ? "Change Password" : "Set Password"}
      onClose={goBack}
    >
      <div className="mx-5 my-8">
        <PasswordChangeForm
          buttonText={user?.hasPassword ? "Change Password" : "Set Password"}
          changePasswordCallback={changePasswordCallback}
          cancelButtonAction={goBack}
        />
      </div>
    </Modal>
  );
}

/**
 * @deprecated  Used by the old billing page only, should be dropped soon after all legacy annual plans have been archived
 */
export function CancelSubscriptionModal() {
  const cancelSubscriptionFetcher = useFetcher();
  const goBack = useCloseModal({
    blockNavigationIf: cancelSubscriptionFetcher.state !== "idle",
  });
  const showCancellationSurvey = useFeatureSwitch(
    "REACT_APP_CANCELLATION_SURVEY_USERS"
  );

  const unpaidInvoicesFetcher = useFetcher();

  useEffect(() => {
    if (unpaidInvoicesFetcher.state === "idle" && !unpaidInvoicesFetcher.data) {
      unpaidInvoicesFetcher.load("/billing-data/invoices/not-paid");
    }
  });

  useEffect(() => {
    if (cancelSubscriptionFetcher.data === "OK" && !showCancellationSurvey) {
      goBack();
    }
  }, [cancelSubscriptionFetcher.data, goBack, showCancellationSurvey]);

  const ModalWrapper = useMemo(
    () =>
      ({ children }: { children: JSX.Element }) => {
        return (
          <Modal headline="Cancel Subscription" onClose={goBack}>
            {children}
          </Modal>
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (
    unpaidInvoicesFetcher.state === "loading" ||
    !unpaidInvoicesFetcher.data
  ) {
    return (
      <ModalWrapper>
        <CheckingUnpaidInvoicesModalContent />
      </ModalWrapper>
    );
  }

  if (
    unpaidInvoicesFetcher.state === "idle" &&
    unpaidInvoicesFetcher.data?.count !== undefined &&
    unpaidInvoicesFetcher.data.count > 0
  ) {
    return (
      <ModalWrapper>
        <DisabledBecauseOfUnpaidInvoicesModalContent
          closeModalCallback={goBack}
        />
      </ModalWrapper>
    );
  }

  if (cancelSubscriptionFetcher.data === "OK" && showCancellationSurvey) {
    // subscription has been cancelled, show the cancellation survey
    return (
      <ModalWrapper>
        <CancellationSurveyModalContent closeModalCallback={goBack} />
      </ModalWrapper>
    );
  }

  if (cancelSubscriptionFetcher.data !== "OK" || !showCancellationSurvey) {
    // subscription is not cancelled yet, or there was an error cancelling it, show the cancellation content
    return (
      <ModalWrapper>
        <CancelSubscriptionModalContent
          fetcher={cancelSubscriptionFetcher}
          closeModalCallback={goBack}
        />
      </ModalWrapper>
    );
  }

  return <></>; // TODO maybe we should better throw an exception here?
}

/**
 * @deprecated  Used by the old billing page only, should be dropped soon after all legacy annual plans have been archived
 */
export function KeepSubscriptionModal() {
  // TODO after the refactoring is done, this will return an ActiveSubscription
  //  to keep backward compatibility we're also preparing for receiving simple 200-OK responses
  const keepSubscriptionFetcher = useFetcher<any | RouteErrors.RouterActionError>();
  const goBack = useCloseModal({
    blockNavigationIf: keepSubscriptionFetcher.state !== "idle",
  });

  const { activeSubscription } = useActiveSubscription();

  useEffect(() => {
    if (
      keepSubscriptionFetcher.data === "OK" ||
      keepSubscriptionFetcher.data?.planSlug !== undefined
    ) {
      goBack();
    }
  }, [goBack, keepSubscriptionFetcher.data]);

  return (
    <Modal headline="Reactivate Subscription" onClose={goBack}>
      <ModalContent>
        <ModalSections>
          <div>
            Your subscription was cancelled and will end on{" "}
            {fmtDate(new Date((activeSubscription?.currentTermEnd || 0) * 1000))}.
          </div>
          <TaggedText
            message="If you have any follow up questions or want to subscribe to a custom plan, please [contact our support team|contact_support]."
            tagCallbacks={{ contact_support: goBack }}
          />
          <div>
            You cancelled your subscription by accident? Just click on the
            "Reactivate subscription" button below and your subscription will be
            renewed with the next cycle.
          </div>
          <ErrorMessage
            errorMessage={useFormattedActionError(keepSubscriptionFetcher.data)}
            closeModalCallback={goBack}
          />
        </ModalSections>
        <keepSubscriptionFetcher.Form
          method="POST"
          action="/billing-data/subscription/keep"
        >
          <ModalButtonsSection>
            <SubmitButton
              text="Reactivate subscription"
              size="MD"
              className="button button-tertiary"
            />
            <Button text="Ok" size="MD" onClick={goBack} />
          </ModalButtonsSection>
        </keepSubscriptionFetcher.Form>
      </ModalContent>
    </Modal>
  );
}

export function CancelSubscriptionChangeModal() {
  const revalidator = useRevalidator();
  const goBack = useCloseModal();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const onSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      await scraperApi.subscription.removeScheduledChanges();
      revalidator.revalidate();
      goBack();
    } catch (error) {
      setError(
        (error as Error).message || "There was an error cancelling your changes"
      );
    } finally {
      setSubmitting(false);
    }
  }, [goBack, revalidator]);

  return (
    <Modal headline="Cancel Subscription Change" onClose={goBack}>
      <ModalContent>
        <ModalSections>
          <div>Are you sure you want to cancel your subscription change?</div>
          <div>Your scheduled changes will be canceled</div>
          <ErrorMessage errorMessage={error} closeModalCallback={goBack} />
        </ModalSections>
        <ModalButtonsSection>
          <Button
            text="Keep Change"
            className="button button-secondary"
            onClick={goBack}
            size="MD"
          />
          <Button
            text="Cancel Change"
            className="button button-primary destructive"
            onClick={onSubmit}
            size="MD"
            loading={submitting}
          />
        </ModalButtonsSection>
      </ModalContent>
    </Modal>
  );
}

export function ConfirmRenewalModal() {
  const revalidator = useRevalidator();
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { activeCoupons } = useActiveCoupons();
  const { getPlan } = usePlanHelpers();
  const { pricingOptions } = usePricingOptions();
  const newPricingOptionsEnabled = useMemo(() => {
    return pricingOptions?.["auto-upgrade"]?.allowed || pricingOptions?.["pay-as-you-go"]?.allowed;
  }, [ pricingOptions ]);

  const methods = useForm();
  const { setError } = methods;

  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });

  const modalOnClose = () => {
    trackBillingClickAway("renew_now");
    goBack();
  };

  const [formError, setFormError] = useState<string>();

  let { planId } = useParams() as { planId: string };

  const onSubmit = useCallback(
    async (data: FieldValues) => {
      if (activeCoupons?.find((c) => c.coupon_code === data.couponCode)) {
        return setError("coupon", {
          type: "custom",
          message: "That coupon has already been used",
        });
      }

      if (!activeSubscription?.planSlug) {
        return setFormError("There is no subscription to renew");
      }

      try {
        await scraperApi.subscription.renew(
          activeSubscription?.chargebeeSubscriptionId,
          [data.couponCode],
          isLegacyAnnualPlan(activeSubscription?.planSlug) ? true : undefined
        );
        revalidator.revalidate();
        trackRenewalChanges("renew_now");
        goBack();
      } catch (error) {
        if (error instanceof ApiError) {
          const apiError = error as ApiError<ApiErrorResponse>;
          if (isCouponError(apiError)) {
            return setError("coupon", {
              type: "custom",
              message: apiError.details?.message || apiError.message,
            });
          }

          if (apiError.error_code === "err_recently_renewed") {
            return setFormError(apiError.message);
          }
        }
      }
    },
    [ goBack, revalidator, setError, activeSubscription, activeCoupons ]
  );

  const currentPlan = getPlan(planId);
  const price = getTotalPriceToInvoice(currentPlan, activeSubscription);
  const isInvoiceNeeded =
    !activeSubscription ||
    activeSubscription.nextRenewalAt === activeSubscription.currentTermEnd; // TODO instead of `currentTermEnd` the code was checking `next_billing_at` but that's not present in the new model

  if (pricingOptions?.renewal?.allowed) {
    return (
      <Modal headline="Renew Subscription (sunsetting)" onClose={modalOnClose}>
        <Form methods={methods} onSubmit={onSubmit}>
          <ModalContent>
            <ModalSections>
              { newPricingOptionsEnabled && <SunsettingRenewalWarningCard /> }
              <div>Are you sure you want to renew your subscription?</div>
              <div>
                By renewing your subscription, we will reset your credit
                counters
                {isInvoiceNeeded && (
                  <>
                    , Invoice{" "}
                    <span className="font-bold">
                      {fmtCurrency(price / 100)}
                    </span>{" "}
                    now
                  </>
                )}
                {isLegacyAnnualPlan(planId) && !isInvoiceNeeded
                  ? " and shorten your annual subscription by one month."
                  : " and set your billing date to today."}
              </div>
              {(user?.canUseAllCoupons ||
                (user?.canUseCoupons && !isLegacyAnnualPlan(currentPlan?.planSlug))) && (
                // coupon code input will check the currently active coupons by default (by calling the `useActiveCoupons` hook)
                <CouponCodeInput
                  targetPlanSlug={planId}
                />
              )}
              <ErrorMessage
                errorMessage={formError}
                closeModalCallback={goBack}
              />
            </ModalSections>
            <ModalButtonsSection>
              <Button
                text="Cancel"
                className="button button-secondary"
                onClick={goBack}
                size="MD"
              />
              {/* TODO replace with <SubmitButton /> */}
              <Button
                text="Renew Subscription"
                className="button button-primary"
                type="submit"
                icon={{ Icon: FormSubmittingSpinner }}
                onClick={() => {
                  setFormError(undefined);
                }}
                size="MD"
              />
            </ModalButtonsSection>
          </ModalContent>
        </Form>
      </Modal>
    );
  } else {
    return (
      <Modal headline="Renew Subscription (sunsetting)" onClose={goBack}>
        <ModalContent>
          <ModalSections>
            <TaggedText message={Messages.renewalDisabledTaggedMessage} />
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Ok"
              className="button button-primary"
              onClick={goBack}
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Modal>
    );
  }
}

function ChangeAnnualModal({ method }: { method: "Upgrade" | "Downgrade" }) {
  const revalidator = useRevalidator();
  const { activeSubscription } = useActiveSubscription();
  const { getPlan } = usePlanHelpers();
  const methods = useForm();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const [error, setError] = useState<string>();

  const methoding = method.toLowerCase().slice(0, -1) + "ing";
  const { planId } = useParams() as { planId: string };
  const targetPlan = getPlan(planId);
  const price = getTotalPriceToInvoice(targetPlan, activeSubscription);

  const onSubmit = useCallback(async () => {
    try {
      await scraperApi.subscription.update(planId, undefined, false);
      revalidator.revalidate();
      goBack();
    } catch (error) {
      const err = error as any;
      setError(
        err.message || `There was an error ${methoding} your subscription`
      );
    }
  }, [planId, revalidator, goBack, setError, methoding]);

  // TODO should add the coupon input here as well
  return (
    <Modal headline={`${method} Subscription`} onClose={goBack}>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalContent>
          <ModalSections>
            <div>
              Are you sure you want to {method.toLowerCase()} your subscription
              to <span className="font-bold capitalize">{targetPlan?.planName}</span>
              ?
            </div>
            <div>
              By {methoding} your subscription, we will reset your credit
              counters and invoice{" "}
              <span className="font-bold">{fmtCurrency(price / 100)}</span> on{" "}
              { activeSubscription?.currentTermEnd ? fmtDate(new Date(activeSubscription?.currentTermEnd! * 1000)) : "next billing date"}, when
              your annual subscription ends.
              <TaggedText
                message={` If you would like to end your annual subscription and ${method.toLowerCase()} earlier, please [contact our support team|contact_support].`}
                tagCallbacks={{ contact_support: goBack }}
              />
            </div>
            <ErrorMessage errorMessage={error} closeModalCallback={goBack} />
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Cancel"
              className="button button-secondary"
              onClick={goBack}
              size="MD"
            />
            {/* TODO replace with <SubmitButton/> */}
            <Button
              text={`${method} Subscription`}
              className="button button-primary"
              type="submit"
              icon={{ Icon: FormSubmittingSpinner }}
              onClick={() => {
                setError(undefined);
              }}
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Form>
    </Modal>
  );
}

export function UpgradeAnnualModal() {
  return <ChangeAnnualModal method="Upgrade" />;
}

export function UpgradeModal() {
  return <OldUpgradeSubscriptionModal/>;
}

export function DowngradeAnnualModal() {
  return <ChangeAnnualModal method="Downgrade" />;
}

export function DowngradeModal() {
  const revalidator = useRevalidator();
  const { activeSubscription } = useActiveSubscription();
  const { getPlan } = usePlanHelpers();

  let { planId } = useParams() as { planId: string };
  const methods = useForm();
  const { handleSubmit } = methods;
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const [formError, setFormError] = useState<string>();

  const currentPlan = getPlan(planId);
  const price = getTotalPriceToInvoice(currentPlan, activeSubscription);
  const onSubmit = useCallback(
    async (data: FieldValues) => {
      try {
        await scraperApi.subscription.update(
          planId,
          undefined,
          false
        );
        revalidator.revalidate();
        goBack();
      } catch (error) {
        setFormError(
          (error as Error).message ||
            "There was an error downgrading your subscription."
        );
      }
    },
    [goBack, planId, revalidator]
  );

  return (
    <Modal headline="Downgrade Subscription" onClose={goBack}>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalContent>
          <ModalSections>
            <div>
              Are you sure you want to downgrade your subscription to{" "}
              <span className="font-bold capitalize">
                {currentPlan?.planName.replace("202206", "").trim()}
              </span>
              ?
            </div>
            <div>
              By downgrading your subscription, we will reset your credit
              counters, Invoice{" "}
              <span className="font-bold">{fmtCurrency(price / 100)} </span> and
              update your billing date either immediately or at the end of
              current cycle.
            </div>

            <ErrorMessage
              errorMessage={formError}
              closeModalCallback={goBack}
            />
          </ModalSections>
          <div className="flex flex-col items-end gap-y-2 gap-x-1">
            {/* TODO replace with <SubmitButton/> */}
            <Button
              text="Downgrade at End of Cycle"
              className="button button-primary destructive"
              icon={
                methods.formState.isSubmitting
                  ? { Icon: FormSubmittingSpinner }
                  : undefined
              }
              onClick={() => {
                setFormError(undefined);
                handleSubmit((d) => onSubmit({ ...d, immediate: false }))();
              }}
              size="SM"
            />
            <div className="flex justify-between w-full">
              <Button
                text="Cancel"
                className="button button-secondary"
                onClick={goBack}
                size="SM"
              />
            </div>
          </div>
        </ModalContent>
      </Form>
    </Modal>
  );
}

export function AutoRenewalModal() {
  const revalidator = useRevalidator();
  const { pricingOptions } = usePricingOptions();
  const newPricingOptionsEnabled = useMemo(() => {
    return pricingOptions?.["auto-upgrade"]?.allowed || pricingOptions?.["pay-as-you-go"]?.allowed;
  }, [ pricingOptions ]);

  const methods = useForm();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const [formError, setFormError] = useState<string>();

  let { ceiling } = useParams() as { ceiling: string };

  const modalOnClose = () => {
    trackBillingClickAway(`auto_renew_${ceiling}`);
    goBack();
  };

  const onSubmit = useCallback(async () => {
    try {
      await scraperApi.subscription.autoRenew(ceiling);
      revalidator.revalidate();
      trackRenewalChanges(`auto_renew_${ceiling}`);
      goBack();
    } catch (error) {
      setFormError("There was an error changing your auto-renewal settings");
    }
  }, [ceiling, goBack, revalidator]);

  if (pricingOptions?.renewal?.allowed) {
    return (
      <Modal headline="Automatic renewals (sunsetting)" onClose={modalOnClose}>
        <Form methods={methods} onSubmit={onSubmit}>
          <ModalContent>
            <ModalSections>
              { newPricingOptionsEnabled && <SunsettingRenewalWarningCard auto /> }
              <div>Are you sure you want to auto renew your subscription?</div>
              <div>
                <p>
                  Your subscription will be renewed automatically after you have
                  used <span className="font-bold">{ceiling}%</span> of your
                  credits.
                </p>
              </div>
              <ErrorMessage
                errorMessage={formError}
                closeModalCallback={goBack}
              />
            </ModalSections>
            <ModalButtonsSection>
              <Button
                text="Cancel"
                className="button button-secondary"
                onClick={goBack}
                size="MD"
              />
              {/* TODO replace with <SubmitButton/> */}
              <Button
                text={`Auto Renew at ${ceiling}%`}
                className="button button-primary"
                type="submit"
                icon={{ Icon: FormSubmittingSpinner }}
                onClick={() => {
                  setFormError(undefined);
                }}
                size="MD"
              />
            </ModalButtonsSection>
          </ModalContent>
        </Form>
      </Modal>
    );
  } else {
    return (
      <Modal headline="Automatic renewals (sunsetting)" onClose={goBack}>
        <ModalContent>
          <ModalSections>
            <TaggedText message={Messages.renewalDisabledTaggedMessage} />
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Ok"
              className="button button-primary"
              onClick={goBack}
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Modal>
    );
  }
}

export function ClearAutoRenewalModal() {
  const revalidator = useRevalidator();
  const { activeSubscription } = useActiveSubscription();
  let nextRenewalAt;

  if (activeSubscription?.nextRenewalAt) {
    nextRenewalAt = `(next renewal ${format(
      activeSubscription.nextRenewalAt * 1000,
      "EEE, MMMM do yyyy"
    )})`;
  }

  const methods = useForm();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const [formError, setFormError] = useState<string>();

  const modalOnClose = () => {
    trackBillingClickAway("standard");
    goBack();
  };

  const onSubmit = useCallback(async () => {
    try {
      await scraperApi.subscription.clearAutoRenew();
      revalidator.revalidate();
      trackRenewalChanges("standard");
      goBack();
    } catch (error) {
      setFormError("There was an error changing your auto-renewal settings");
    }
  }, [goBack, revalidator]);

  return (
    <Modal headline="Standard renewal cycle" onClose={modalOnClose}>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalContent>
          <ModalSections>
            <p>
              Are you sure you want to set your renewal to the default renewal
              settings?
            </p>
            <p>
              Your subscription will be renewed by the end of your billing cycle
              {nextRenewalAt ? ` ${nextRenewalAt}, ` : ", "}
              even if you run out of all your credits before the end of the
              billing cycle.
            </p>
            <p>
              You can learn more about our renewal feature and other options in
              our{" "}
              <ExtLink
                className="underline"
                to="https://docs.scraperapi.com/faq/plans-and-billing/what-happens-if-i-run-out-of-credits-before-the-end-of-my-current-subscription"
              >
                documentation
              </ExtLink>
              .
            </p>
            <ErrorMessage
              errorMessage={formError}
              closeModalCallback={goBack}
            />
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Cancel"
              className="button button-secondary"
              onClick={goBack}
              size="MD"
            />
            {/* TODO replace with <SubmitButton/> */}
            <Button
              text="OK"
              className="button button-primary"
              type="submit"
              icon={{ Icon: FormSubmittingSpinner }}
              onClick={() => {
                setFormError(undefined);
              }}
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Form>
    </Modal>
  );
}

export function CouponsModal() {
  const revalidator = useRevalidator();
  const { activeCoupons } = useActiveCoupons();
  const methods = useForm();
  const { setError, clearErrors } = methods;
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });

  const checkAlreadyUsedCoupon = useCallback(
    (couponCode: string) => {
      if (activeCoupons?.find((c) => c.coupon_code === couponCode)) {
        setError("coupon", {
          type: "custom",
          message: "That coupon has already been used",
        });
        return true;
      }

      clearErrors("coupon");
      return false;
    },
    [ activeCoupons, setError, clearErrors ]
  );

  const onSubmit = useCallback(
    async (data: FieldValues) => {
      if (!checkAlreadyUsedCoupon(data.couponCode)) {
        try {
          await scraperApi.auth.coupons(data.couponCode);
          revalidator.revalidate();
          goBack();
        } catch (error) {
          const err = error as any;
          return setError("coupon", {
            type: "custom",
            message: err.message || "There was an error adding the coupon",
          });
        }
      }
    },
    [goBack, revalidator, setError, checkAlreadyUsedCoupon]
  );

  return (
    <Modal headline="Manage Coupons" onClose={goBack}>
      <Form methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col p-5 space-y-4">
          <div className="flex flex-col space-y-6 text-gray dark:text-neutral-600">
            { activeCoupons?.map((coupon, index) => {
              return (
                <div
                  key={`${coupon.coupon_code}-${coupon.coupon_type}-${index}`}
                >
                  <div className="flex justify-between">
                    <div className="flex flex-col">
                      <div className="font-bold">{coupon.coupon_code}</div>
                      <div className="text-sm">
                        {coupon.added_at ? (
                          <>Active since {fmtDate(new Date(coupon.added_at))}</>
                        ) : (
                          <>&nbsp;</>
                        )}
                      </div>
                    </div>
                    <div className="">{ coupon.discount_type === "fixed_amount" ? ("-$" + coupon.discount_amount) : ("-" + coupon.discount_value + "%") }</div>
                  </div>
                </div>
              );
            })}
            { activeCoupons?.length && (
              <hr className="text-borderColor dark:text-neutral-200" />
            )}
          </div>
          {/* CouponCodeInput will check the active coupons by default by also calling the `useActiveCoupons` hook */}
          <CouponCodeInput
            className="space-y-2"
          />
          <ModalButtonsSection>
            <Button
              text="Dismiss"
              className="button button-secondary"
              onClick={goBack}
              size="MD"
            />
            {/* TODO replace with <SubmitButton/> */}
            <Button
              text="Add coupon"
              className="button button-primary"
              type="submit"
              icon={{ Icon: FormSubmittingSpinner }}
              onClick={() => {}}
              size="MD"
            />
          </ModalButtonsSection>
        </div>
      </Form>
    </Modal>
  );
}

export function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState([]);
  const [removing, setRemoving] = useState(false);
  const methods = useForm();
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      const apiKeys = await scraperApi.apiKeys.fetchApiKeys({
        signal: controller.signal,
      });
      setApiKeys(apiKeys);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const removeApiKeyCallback = useCallback(
    async (apiKey: string) => {
      setRemoving(true);
      try {
        await scraperApi.apiKeys.removeApiKey(apiKey);
        setApiKeys(apiKeys.filter((key: any) => key.apiKey !== apiKey));
      } finally {
        setRemoving(false);
      }
    },
    [apiKeys, setApiKeys, setRemoving]
  );

  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const statefulNavigate = useStatefulNavigate();
  const goToConfirm = useCallback(
    () => statefulNavigate("/api-keys/confirm"),
    [statefulNavigate]
  );

  return (
    <Modal headline="Manage Api Keys" onClose={goBack}>
      <div className="flex flex-col p-5 space-y-4">
        <div className="flex flex-col space-y-6 text-gray dark:text-neutral-600">
          {apiKeys.length === 0 && (
            <Spinner className="self-center w-5 h-5 mr-3 -ml-1 text-black animate-spin" />
          )}
          {apiKeys.map((key: any) => {
            return (
              <div key={key.apiKey}>
                <div className="flex justify-between">
                  <div className="flex flex-col">
                    <div className="font-mono font-bold">{key.apiKey}</div>

                    {key.expireAt ? (
                      <div className="text-sm text-red dark:text-error-600">
                        Expires at{" "}
                        {key.expireAt && fmtDate(new Date(key.expireAt))}
                      </div>
                    ) : (
                      <div className="text-sm">
                        Active since{" "}
                        {key.addedAt && fmtDate(new Date(key.addedAt))}
                      </div>
                    )}
                  </div>
                  {key.expireAt && (
                    <div className="flex items-center">
                      {removing ? (
                        <Spinner className="w-5 h-5 animate-spin" />
                      ) : (
                        <TrashIcon
                          className="cursor-pointer text-black/30 hover:text-black"
                          onClick={async () => {
                            return removeApiKeyCallback(key.apiKey);
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <ModalButtonsSection>
          <Button
            text="New API Key"
            className="button button-primary"
            onClick={goToConfirm}
            size="MD"
          />
        </ModalButtonsSection>
      </div>
    </Modal>
  );
}

export function ConfirmApiKeyChangeModal() {
  const revalidator = useRevalidator();
  const [error, setError] = useState<string>();
  const statefulNavigate = useStatefulNavigate();
  const goBack = useCallback(
    () => statefulNavigate("/api-keys"),
    [statefulNavigate]
  );
  const user = useUser();

  const onSubmit = useCallback(async () => {
    try {
      await scraperApi.apiKeys.newApiKey();
      revalidator.revalidate();
      goBack();
    } catch (error) {
      setError(
        (error as Error).message || "There was an error updating your API key"
      );
    }
  }, [goBack, revalidator]);

  return (
    <Modal headline="Confirm New API Key" onClose={goBack}>
      <ModalContent>
        <ModalSections>
          <div>Are you sure you want to generate a new API key?</div>
          {user?.apiKeyExpirationDays !== undefined &&
            user.apiKeyExpirationDays > 0 && (
              <div>
                Your current key will remain active for{" "}
                {user.apiKeyExpirationDays} more day
                {user.apiKeyExpirationDays > 1 ? "s" : ""}. After that it will
                be disabled and you will only be able to scrape with the new
                key.
              </div>
            )}
          {(user?.apiKeyExpirationDays || 0) === 0 && (
            <div>
              Your current key will be disabled immediately, so you won't be
              able to use that for scraping anymore.
            </div>
          )}
          <ErrorMessage errorMessage={error} closeModalCallback={goBack} />
        </ModalSections>
        <ModalButtonsSection>
          <Button
            text="Cancel"
            className="button button-secondary"
            onClick={goBack}
            size="MD"
          />
          <Button
            text="OK"
            className="button button-primary"
            onClick={onSubmit}
            size="MD"
          />
        </ModalButtonsSection>
      </ModalContent>
    </Modal>
  );
}

export function CancelEnterpriseModal() {
  const goBack = useCloseModal();
  const { activeSubscription } = useActiveSubscription();

  return (
    <Modal headline="Cancel Subscription" onClose={goBack}>
      <ModalContent>
        <div className="text-gray dark:text-neutral-600">
          <TaggedText
            message={
              (activeSubscription?.planName || "Enterprise") +
              " subscriptions can not be cancelled manually. Please reach out to our [support team|contact_support]"
            }
          />
          <span>
            {" "}
            or send an email to{" "}
            <a
              className="text-brandPrimary dark:text-primary-600 hover:text-brandPrimary-900 dark:hover:text-primary-700"
              href="mailto:support@scraperapi.com"
            >
              support@scraperapi.com
            </a>
          </span>
        </div>
        <ModalButtonsSection>
          <Button
            text="Ok"
            className="button button-primary"
            onClick={goBack}
            size="MD"
          />
        </ModalButtonsSection>
      </ModalContent>
    </Modal>
  );
}

export function PayNowModal() {
  const { generatePayNowLink } = useChargebeePortals();
  const methods = useForm();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const { openChargebeePortal } = useChargebeePortals();
  const revalidator = useRevalidator();
  // TODO this deprecated hook should be replaced by v2's useAllIssues or useStatuspageIssues hook. to refresh the issues, an action
  //  should be created and called, so the react router will refresh the issues fetcher automatically
  const issuesContext = useIssues();

  const [payNowLinkOpened, setPayNowLinkOpened] = useState<boolean>();

  const onSubmit = useCallback(async () => {
    const payNowLink = await generatePayNowLink();

    window.open(payNowLink, "_blank");

    setPayNowLinkOpened(true);
  }, [setPayNowLinkOpened, generatePayNowLink]);

  const onDone = useCallback(async () => {
    revalidator.revalidate();
    issuesContext.refresh(undefined);
    goBack();
  }, [revalidator, issuesContext, goBack]);

  return (
    <Modal headline="Pay Now" onClose={goBack}>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalContent>
          <div className="text-gray dark:text-neutral-600">
            We weren't able to collect all of your invoices yet. Please make
            sure your payment method is correct and you have sufficient funds.
          </div>
          <ModalButtonsSection>
            <Button
              text="Check payment method"
              className="button button-secondary"
              onClick={() => openChargebeePortal("PAYMENT_SOURCES")}
              size="MD"
            />
            {/* TODO replace with <SubmitButton/> */}
            <Button
              text="Pay now"
              className="button button-primary"
              disabled={payNowLinkOpened}
              type="submit"
              icon={{ Icon: FormSubmittingSpinner }}
              onClick={() => {}}
              size="MD"
            />
            <Button
              text="Done"
              className="button button-secondary"
              onClick={onDone}
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Form>
    </Modal>
  );
}

export function BookSalesCallModal() {
  const methods = useForm();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });

  return (
    <>
      <Modal
        headline="Book a Sales Call for 30 Extra Trial Days"
        onClose={goBack}
      >
        <BookSalesCallModalContent />
      </Modal>
    </>
  );
}

export function DeleteAccountModal() {
  const methods = useForm();
  const revalidator = useRevalidator();
  const goBack = useCloseModal({
    blockNavigationIf: methods.formState.isSubmitting,
  });
  const navigate = useNavigate();

  const onSubmit = useCallback(async () => {
    try {
      const response = await scraperApi.auth.requestDeletion();

      let toasterMessage =
        "You sent a request to our support team to delete your account. Our team will send you a confirmation email, when your account was deleted.";

      if (response === "deleted_permanently") {
        toasterMessage =
          "Your request to delete your account was successful. In a few seconds you will be logged out and can not log in anymore.";
        setTimeout(async () => {
          await scraperApi.auth.logout();
          navigate("/login");
        }, 6000);
      }

      Toaster.success("Account Deletion Requested", toasterMessage);
    } catch (error) {
      Toaster.error(
        "Requesting Account Deletion Failed",
        "There was an issue with your request. Please try again later or contact our support team."
      );
    }

    goBack();

    revalidator.revalidate();
  }, [goBack, navigate, revalidator]);

  return (
    <Modal headline="Request Account Deletion" onClose={goBack}>
      <Form onSubmit={onSubmit} methods={methods}>
        <ModalContent>
          <ModalSections>
            <div>Are you sure you want to delete your account?</div>
            <div>
              By deleting your account you won't be able to login anymore and
              your API key will be invalid.
            </div>
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Keep Account"
              className="button button-secondary"
              onClick={goBack}
              size="MD"
            />
            {/* TODO replace with <SubmitButton /> */}
            <Button
              text="Request account deletion"
              className="button button-primary destructive"
              type="submit"
              size="MD"
              icon={{ Icon: FormSubmittingSpinner }}
              onClick={() => {}}
            />
          </ModalButtonsSection>
        </ModalContent>
      </Form>
    </Modal>
  );
}

export function FiltersModal() {
  const closeModal = useCloseModal({ goBackInHistoryOnClose: true });

  return (
    <Modal headline="Filters" onClose={closeModal} showOverflow>
      <FiltersModalContent closeModalCallback={closeModal} />
    </Modal>
  );
}

export function CustomizeColumnsModal() {
  const closeModal = useCloseModal({ goBackInHistoryOnClose: true });
  const location = useLocation();
  const state = location.state as {
    tableHeaders?: { key: string; label: string; tooltip?: string }[];
    tableType?: "domain" | "error";
  };

  // Determine table type from route if not explicitly provided
  const tableType = state?.tableType ||
    (location.pathname.includes("error") ? "error" : "domain");

  // Fallback to TABLE_CONFIGS if state is missing (e.g. direct URL navigation or refresh)
  const tableHeaders = state?.tableHeaders ||
    TABLE_CONFIGS[tableType === "error" ? "error" : "domain"].headers;

  return (
    <Modal headline="Customize columns" onClose={closeModal}>
      <CustomizeColumnsModalContent
        onCancel={closeModal}
        tableHeaders={tableHeaders}
        tableType={tableType}
      />
    </Modal>
  );
}

export function ErrorFiltersModal() {
  const closeModal = useCloseModal({ goBackInHistoryOnClose: true });
  const { errorLogsFilters, setErrorLogsFilters } = useAnalyticsFilters();
  const { errorLogsData } = useErrorLogs();

  // Get filter options from the error logs data (already fetched by provider)
  const filterOptions = errorLogsData.filterOptions;

  // Local draft state - only committed when "Apply" is clicked
  const [draftFilters, setDraftFilters] = useState<ErrorFilterSelection>({
    domain: errorLogsFilters.domains[0] || "",
    statusCode: errorLogsFilters.statusCodes[0] || "",
    severity: errorLogsFilters.severity || "",
  });

  const handleChange = useCallback((updates: Partial<ErrorFilterSelection>) => {
    setDraftFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleReset = useCallback(() => {
    setDraftFilters({
      domain: "",
      statusCode: "",
      severity: "",
    });
  }, []);

  const handleApply = useCallback(() => {
    const newFilters = {
      domains: draftFilters.domain ? [draftFilters.domain] : [],
      statusCodes: draftFilters.statusCode ? [draftFilters.statusCode] : [],
      severity: draftFilters.severity
        ? draftFilters.severity.toLowerCase() as SeverityLevel
        : null,
    };
    setErrorLogsFilters(newFilters);
    closeModal();
  }, [draftFilters, setErrorLogsFilters, closeModal]);

  return (
    <Modal headline="Filters" onClose={closeModal} showOverflow={true}>
      <ErrorFiltersModalContent
        domains={filterOptions.domains}
        statusCodes={filterOptions.statusCodes}
        severities={SEVERITY_LEVELS}
        selected={draftFilters}
        onReset={handleReset}
        onChange={handleChange}
        onCancel={closeModal}
        onApply={handleApply}
      />
    </Modal>
  );
}
